import { useCallback, useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import { useClickAway } from "react-use";

import { theme } from "@/style";
import {
    Wrapper,
    Label,
    Trigger,
    TriggerValue,
    TriggerPlaceholder,
    Indicator,
    Menu,
    SearchInput,
    OptionList,
    Option,
    Error,
} from "./AsyncSelect.styled";

function ChevronIcon({ isOpen }) {
    const fill = theme.PrimaryGrey;
    return (
        <svg
            width="12"
            height="12"
            viewBox="0 0 12 12"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            style={{ transform: isOpen ? "rotate(180deg)" : "rotate(0deg)", transition: "transform 0.2s ease" }}
        >
            <path
                d="M3 4.5L6 7.5L9 4.5"
                stroke={fill}
                strokeWidth="1.25"
                strokeLinecap="round"
                strokeLinejoin="round"
            />
        </svg>
    );
}

const DEFAULT_DEBOUNCE_MS = 400;

const defaultGetOptionValue = (opt) => opt.value;
const defaultGetOptionLabel = (opt) => opt.label;

export default function AsyncSelect({
    value,
    onChange,
    loadOptions,
    label,
    placeholder = "Select...",
    disabled = false,
    isLoading = false,
    error,
    debounceMs = DEFAULT_DEBOUNCE_MS,
    noOptionsMessage = "No options",
    getOptionValue = defaultGetOptionValue,
    getOptionLabel = defaultGetOptionLabel,
    block = true,
    loadDefaultOptionsOnOpen = true,
    defaultOptionsDependency,
}) {
    const [isOpen, setIsOpen] = useState(false);
    const [searchInputValue, setSearchInputValue] = useState("");
    const [options, setOptions] = useState([]);
    const [loading, setLoading] = useState(false);
    const wrapperRef = useRef(null);
    const debounceRef = useRef(null);
    const selectedOptionRef = useRef(null);

    useClickAway(wrapperRef, () => setIsOpen(false));

    const performLoad = useCallback(
        (inputValue, callback) => {
            if (typeof loadOptions !== "function") {
                callback?.([]);
                return;
            }
            setLoading(true);
            loadOptions(inputValue, (opts) => {
                setLoading(false);
                const nextOptions = Array.isArray(opts) ? opts : [];
                setOptions(nextOptions);
                callback?.(nextOptions);
            });
        },
        [loadOptions],
    );

    const debouncedLoad = useCallback(
        (inputValue) => {
            if (debounceRef.current) clearTimeout(debounceRef.current);
            debounceRef.current = setTimeout(() => {
                performLoad(inputValue);
            }, debounceMs);
        },
        [performLoad, debounceMs],
    );

    useEffect(() => {
        if (value == null || value === "") {
            selectedOptionRef.current = null;
        }
    }, [value]);

    useEffect(() => {
        return () => {
            if (debounceRef.current) clearTimeout(debounceRef.current);
        };
    }, []);

    useEffect(() => {
        if (
            isOpen &&
            loadDefaultOptionsOnOpen &&
            options.length === 0 &&
            !loading &&
            searchInputValue === ""
        ) {
            performLoad("");
        }
    }, [isOpen, loadDefaultOptionsOnOpen, performLoad, loading, searchInputValue, options.length]);

    const prevDependencyRef = useRef(defaultOptionsDependency);
    useEffect(() => {
        if (prevDependencyRef.current !== defaultOptionsDependency) {
            setOptions([]);
            selectedOptionRef.current = null;
            prevDependencyRef.current = defaultOptionsDependency;
        }
    }, [defaultOptionsDependency]);

    useEffect(() => {
        // Only load default options when options are empty AND user has not typed a search.
        // Otherwise a search that returns 0 results would trigger this and overwrite with default list.
        if (
            defaultOptionsDependency &&
            options.length === 0 &&
            !loading &&
            searchInputValue === ""
        ) {
            performLoad("");
        }
    }, [defaultOptionsDependency, options.length, loading, performLoad, searchInputValue]);

    const handleSearchChange = (e) => {
        const next = e.target.value;
        setSearchInputValue(next);
        debouncedLoad(next);
    };

    const selectedOption =
        options.find((o) => getOptionValue(o) === value) ||
        selectedOptionRef.current ||
        (value != null && value !== "" ? { value, label: String(value) } : null);

    const displayLabel = selectedOption ? getOptionLabel(selectedOption) : "";
    const showLoading = loading || isLoading;

    const renderTriggerContent = () => {
        if (showLoading) return <TriggerValue>Loading...</TriggerValue>;
        if (displayLabel) return <TriggerValue>{displayLabel}</TriggerValue>;
        return <TriggerPlaceholder>{placeholder}</TriggerPlaceholder>;
    };

    const handleTriggerClick = () => {
        if (disabled) return;
        setIsOpen((prev) => !prev);
        if (!isOpen) {
            setSearchInputValue("");
            debouncedLoad("");
        }
    };

    const handleOptionClick = (option) => {
        selectedOptionRef.current = option;
        onChange(option);
        setIsOpen(false);
        setSearchInputValue("");
    };

    return (
        <Wrapper ref={wrapperRef} $block={block} className="async-select">
            {label && <Label>{label}</Label>}
            <Trigger
                type="button"
                $disabled={disabled}
                $isOpen={isOpen}
                onClick={handleTriggerClick}
            >
                {renderTriggerContent()}
                <Indicator $isOpen={isOpen}>
                    <ChevronIcon isOpen={isOpen} />
                </Indicator>
            </Trigger>
            {isOpen && (
                <Menu>
                    <SearchInput
                        type="text"
                        value={searchInputValue}
                        onChange={handleSearchChange}
                        placeholder="Type to search..."
                        autoFocus
                        onClick={(e) => e.stopPropagation()}
                    />
                    <OptionList>
                        {options.length === 0 && !showLoading ? (
                            <Option as="div" $empty>
                                {noOptionsMessage}
                            </Option>
                        ) : (
                            options.map((option) => {
                                const optionValue = getOptionValue(option);
                                const isSelected = optionValue === value;
                                return (
                                    <Option
                                        key={optionValue}
                                        $selected={isSelected}
                                        onClick={() => handleOptionClick(option)}
                                    >
                                        {getOptionLabel(option)}
                                    </Option>
                                );
                            })
                        )}
                    </OptionList>
                </Menu>
            )}
            {error && <Error>{error}</Error>}
        </Wrapper>
    );
}

AsyncSelect.propTypes = {
    value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    onChange: PropTypes.func.isRequired,
    loadOptions: PropTypes.func.isRequired,
    label: PropTypes.string,
    placeholder: PropTypes.string,
    disabled: PropTypes.bool,
    isLoading: PropTypes.bool,
    error: PropTypes.string,
    debounceMs: PropTypes.number,
    noOptionsMessage: PropTypes.string,
    getOptionValue: PropTypes.func,
    getOptionLabel: PropTypes.func,
    block: PropTypes.bool,
    loadDefaultOptionsOnOpen: PropTypes.bool,
    defaultOptionsDependency: PropTypes.any,
};
